import { useState } from "react";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { CtaButton } from "./CtaButton";
import { priceCta } from "@/config/site";

export const NAV_ITEMS = [
  { id: "o-knize", label: "O knize" },
  { id: "co-ziskas", label: "Co získáš" },
  { id: "obsah", label: "40 principů" },
  { id: "ukazky", label: "Ukázky" },
  { id: "autor", label: "O autorovi" },
  { id: "faq", label: "FAQ" },
];

export const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -72 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
};

export const Header = () => {
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  const location = useLocation();
  const navigate = useNavigate();

  useMotionValueEvent(scrollY, "change", (y) => {
    const prev = scrollY.getPrevious() ?? 0;
    setHidden(y > prev && y > 160 && !open);
    setScrolled(y > 24);
  });

  const go = (e, id) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    scrollToSection(id);
  };

  const goHome = (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      return;
    }
    window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        animate={{ y: hidden ? "-100%" : 0 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
          scrolled
            ? "border-b border-white/[0.06] bg-navy-deep/80 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
          <a
            href="/"
            onClick={goHome}
            data-testid="header-logo"
            className="font-serif text-xl tracking-wide text-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-copper"
          >
            Proměna <span className="text-copper">muže</span>
          </a>

          <nav aria-label="Hlavní navigace" className="hidden items-center gap-7 lg:flex">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.id}
                href={`/#${item.id}`}
                onClick={(e) => go(e, item.id)}
                data-testid={`nav-${item.id}`}
                className="text-sm text-cream/70 transition-colors duration-200 hover:text-copper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-copper"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <CtaButton event="header_cta" location="header" size="sm" testId="header-cta" className="hidden sm:inline-flex">
              {priceCta}
            </CtaButton>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-label={open ? "Zavřít menu" : "Otevřít menu"}
              data-testid="mobile-menu-toggle"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-cream transition-colors hover:border-copper lg:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 flex flex-col bg-navy-deep/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <nav aria-label="Mobilní navigace" className="flex flex-1 flex-col justify-center gap-2 px-8 pt-24">
              {NAV_ITEMS.map((item, i) => (
                <motion.a
                  key={item.id}
                  href={`/#${item.id}`}
                  onClick={(e) => go(e, item.id)}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.35 }}
                  data-testid={`mobile-nav-${item.id}`}
                  className="border-b border-white/[0.06] py-4 font-serif text-2xl text-cream transition-colors hover:text-copper"
                >
                  {item.label}
                </motion.a>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.35 }}
                className="pt-8"
              >
                <CtaButton event="header_cta" location="mobile-menu" size="md" testId="mobile-menu-cta" className="w-full">
                  {priceCta}
                </CtaButton>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
